import * as LDClient from 'launchdarkly-js-client-sdk';

import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';

import { Features } from '../../types/features';
import { useSelectUserContext } from '../../selectors/userContext.selectors';
import { setFeatures } from '../../actions/app.actions';
import { useSiteConfig } from '../../contexts';
import { useSelectFeaturesLoaded, useSelectFeatures } from '../../selectors';
import { setFeatureFlags } from '@innovyze/lib_get_service';

interface FeatureFlagContext {
	features: Features;
	featuresLoaded: boolean;
}

export const FeatureFlagContext = React.createContext<FeatureFlagContext>({
	features: {},
	featuresLoaded: false,
});

export const useFeatureFlags = (): FeatureFlagContext =>
	useContext(FeatureFlagContext);

interface FeatureFlagProviderProps {
	children: React.ReactElement | React.ReactElement[] | null;
}

let ldclient: LDClient.LDClient;

/**
 * Feature flag wrapper for Launch Darkly
 *
 * Features are kabob cased, like 'info-360-alerts'
 *
 * The current implementation is environment-specific, not segmented by users
 *
 * Launch Darkly does not initialize until the user has been loaded
 */
export const FeatureFlagProvider = ({
	children,
}: FeatureFlagProviderProps): JSX.Element | null => {
	const { configLoaded, config } = useSiteConfig();
	const {
		id,
		email,
		defaultSite: siteId,
		defaultTeamId: teamUrn,
	} = useSelectUserContext();
	const featuresLoaded = useSelectFeaturesLoaded();
	const features = useSelectFeatures();

	const dispatch = useDispatch();
	const clientSideID = config?.launchDarkly.clientSideId;
	const liveUpdates = config?.launchDarkly.liveUpdates;

	const updateFlags = () => {
		const flags = ldclient.allFlags();
		dispatch(setFeatures(flags));
		setFeatureFlags(flags);
	};

	React.useEffect(() => {
		if (configLoaded && !ldclient) {
			if (!clientSideID) {
				dispatch(setFeatures({}));
				// If we don't have a client side ID, we can't initialize the client
			} else if (id && email) {
				// Wait to initialize until we have user context
				ldclient = LDClient.initialize(
					clientSideID,
					{
						key: id,
						email,
						custom: { siteId, teamUrn },
					},
					{ streaming: !!liveUpdates },
				);
				// The client has finished starting up. This event will be sent regardless
				// of whether it successfully connected to LaunchDarkly.
				ldclient.on('ready', updateFlags);
				if (liveUpdates) {
					ldclient.on('change', updateFlags);
				}
			}
		}
	}, [clientSideID, id, email, configLoaded]);

	return (
		<FeatureFlagContext.Provider value={{ features, featuresLoaded }}>
			{children}
		</FeatureFlagContext.Provider>
	);
};

export default FeatureFlagProvider;
